import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Typography,
  IconButton,
  Chip,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as ApproveIcon,
  Block as RejectIcon,
  Assignment as AssignIcon,
  SwapHoriz as ReassignIcon,
  TrendingUp as BoostIcon,
  Schedule as ExtendIcon,
  Restore as ReviveIcon,
  Send as ResubmitIcon,
  RemoveCircle as DropIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import TabPanel, { a11yProps } from '../../components/common/TabPanel';
import ApiService from '../../services/api';

export default function Jobs() {
  const navigate = useNavigate();
  const [value, setValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedRows, setSelectedRows] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: '', title: '', message: '' });
  const [reviewDialog, setReviewDialog] = useState({ open: false, jobId: null, action: '', reviewNotes: '' });

  // Fetch jobs from API
  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('Fetching jobs...');
      const response = await ApiService.getJobs({
        page: 1,
        limit: 1000, // Get all jobs for admin view
        sortBy: 'createdAt',
        sortOrder: 'desc',
        includeAll: true // Include all statuses for admin view
      });
      
      console.log('Jobs API response:', response);
      console.log('Jobs array:', response.jobs);
      console.log('Jobs count:', response.jobs?.length || 0);
      
      if (response.jobs && response.jobs.length > 0) {
        console.log('Job statuses:', response.jobs.map(job => job.status));
        console.log('Pending jobs:', response.jobs.filter(job => job.status === 'pending'));
      }
      
      setJobs(response.jobs || []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setError('Failed to load jobs. Please try again later.');
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleApprove = (jobId) => {
    setReviewDialog({ open: true, jobId, action: 'approve', reviewNotes: '' });
  };

  const handleReject = (jobId) => {
    setReviewDialog({ open: true, jobId, action: 'reject', reviewNotes: '' });
  };

  const handleReviewAction = async () => {
    try {
      const { jobId, action, reviewNotes } = reviewDialog;
      
      if (action === 'approve') {
        await ApiService.approveJob(jobId, reviewNotes);
        setSnackbar({ open: true, message: 'Job approved successfully', severity: 'success' });
      } else if (action === 'reject') {
        if (!reviewNotes.trim()) {
          setSnackbar({ open: true, message: 'Review notes are required for rejection', severity: 'error' });
          return;
        }
        await ApiService.rejectJob(jobId, reviewNotes);
        setSnackbar({ open: true, message: 'Job rejected successfully', severity: 'success' });
      }
      
      fetchJobs(); // Refresh the jobs list
      setReviewDialog({ open: false, jobId: null, action: '', reviewNotes: '' });
    } catch (error) {
      console.error(`Error ${reviewDialog.action}ing job:`, error);
      setSnackbar({ open: true, message: `Failed to ${reviewDialog.action} job: ${error.message}`, severity: 'error' });
    }
  };

  const handleEdit = (jobId) => {
    navigate(`/admin/jobs/${jobId}`);
  };

  const handleDelete = async (jobId) => {
    try {
      await ApiService.deleteJob(jobId);
      setSnackbar({ open: true, message: 'Job deleted successfully', severity: 'success' });
      fetchJobs(); // Refresh the jobs list
    } catch (error) {
      console.error('Error deleting job:', error);
      setSnackbar({ open: true, message: 'Failed to delete job', severity: 'error' });
    }
  };

  // Bulk action handlers
  const getSelectedJobsStatuses = () => {
    const selectedJobs = jobs.filter(job => selectedRows.includes(job._id));
    return [...new Set(selectedJobs.map(job => job.status))];
  };

  const getAvailableBulkActions = () => {
    const statuses = getSelectedJobsStatuses();
    if (statuses.length === 0) return [];
    
    // If multiple different statuses are selected, only show common actions
    if (statuses.length > 1) {
      return ['delete']; // Only delete is available for mixed selections
    }
    
    const status = statuses[0];
    switch (status) {
      case 'pending':
        return ['assign'];
      case 'under_review':
        return ['reassign', 'approve', 'reject'];
      case 'approved':
        return ['drop', 'boost', 'extend'];
      case 'rejected':
        return ['revive', 'delete'];
      case 'dropped':
        return ['resubmit', 'delete'];
      default:
        return ['delete'];
    }
  };

  const handleBulkAction = (action) => {
    const selectedJobs = jobs.filter(job => selectedRows.includes(job._id));
    const jobTitles = selectedJobs.map(job => job.title).join(', ');
    
    let title, message;
    switch (action) {
      case 'assign':
        title = 'Assign Jobs';
        message = `Are you sure you want to assign the following jobs for review: ${jobTitles}?`;
        break;
      case 'reassign':
        title = 'Reassign Jobs';
        message = `Are you sure you want to reassign the following jobs: ${jobTitles}?`;
        break;
      case 'approve':
        title = 'Approve Jobs';
        message = `Are you sure you want to approve the following jobs: ${jobTitles}?`;
        break;
      case 'reject':
        title = 'Reject Jobs';
        message = `Are you sure you want to reject the following jobs: ${jobTitles}?`;
        break;
      case 'drop':
        title = 'Drop Jobs';
        message = `Are you sure you want to drop the following jobs: ${jobTitles}?`;
        break;
      case 'boost':
        title = 'Boost Jobs';
        message = `Are you sure you want to boost the following jobs: ${jobTitles}?`;
        break;
      case 'extend':
        title = 'Extend Jobs';
        message = `Are you sure you want to extend the following jobs: ${jobTitles}?`;
        break;
      case 'revive':
        title = 'Revive Jobs';
        message = `Are you sure you want to revive the following jobs: ${jobTitles}?`;
        break;
      case 'resubmit':
        title = 'Resubmit Jobs';
        message = `Are you sure you want to resubmit the following jobs: ${jobTitles}?`;
        break;
      case 'delete':
        title = 'Delete Jobs';
        message = `Are you sure you want to permanently delete the following jobs: ${jobTitles}? This action cannot be undone.`;
        break;
      default:
        return;
    }
    
    setConfirmDialog({ open: true, action, title, message });
  };

  const executeBulkAction = async () => {
    const { action } = confirmDialog;
    try {
      // Use the new bulk action endpoint
      await ApiService.bulkActionJobs(selectedRows, action, `Bulk ${action} action performed by admin`);
      
      setSnackbar({ 
        open: true, 
        message: `Successfully ${action}ed ${selectedRows.length} job(s)`, 
        severity: 'success' 
      });
      setSelectedRows([]);
      fetchJobs();
    } catch (error) {
      console.error(`Error executing bulk ${action}:`, error);
      setSnackbar({ 
        open: true, 
        message: `Failed to ${action} jobs`, 
        severity: 'error' 
      });
    } finally {
      setConfirmDialog({ open: false, action: '', title: '', message: '' });
    }
  };

  const renderBulkActionButton = (action) => {
    const buttonProps = {
      size: 'small',
      variant: 'contained',
      onClick: () => handleBulkAction(action),
      sx: { mr: 1, mb: 1 }
    };

    switch (action) {
      case 'assign':
        return <Button {...buttonProps} color="primary" startIcon={<AssignIcon />}>Assign</Button>;
      case 'reassign':
        return <Button {...buttonProps} color="info" startIcon={<ReassignIcon />}>Reassign</Button>;
      case 'approve':
        return <Button {...buttonProps} color="success" startIcon={<ApproveIcon />}>Approve</Button>;
      case 'reject':
        return <Button {...buttonProps} color="error" startIcon={<RejectIcon />}>Reject</Button>;
      case 'drop':
        return <Button {...buttonProps} color="warning" startIcon={<DropIcon />}>Drop</Button>;
      case 'boost':
        return <Button {...buttonProps} color="secondary" startIcon={<BoostIcon />}>Boost</Button>;
      case 'extend':
        return <Button {...buttonProps} color="info" startIcon={<ExtendIcon />}>Extend</Button>;
      case 'revive':
        return <Button {...buttonProps} color="success" startIcon={<ReviveIcon />}>Revive</Button>;
      case 'resubmit':
        return <Button {...buttonProps} color="primary" startIcon={<ResubmitIcon />}>Resubmit</Button>;
      case 'delete':
        return <Button {...buttonProps} color="error" startIcon={<DeleteIcon />}>Delete</Button>;
      default:
        return null;
    }
  };

  const columns = [
    { field: 'title', headerName: 'Job Title', width: 200 },
    { field: 'company', headerName: 'Company', width: 150 },
    { field: 'location', headerName: 'Location', width: 150 },
    { field: 'type', headerName: 'Type', width: 120 },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={
            params.value === 'approved' ? 'success' :
            params.value === 'pending' ? 'warning' :
            params.value === 'rejected' ? 'error' :
            params.value === 'closed' ? 'default' :
            params.value === 'deleted' ? 'default' : 'default'
          }
          size="small"
        />
      ),
    },
    { 
      field: 'createdAt', 
      headerName: 'Posted Date', 
      width: 150,
      valueFormatter: (params) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleDateString();
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: value === 1 ? 300 : 200, // Wider for pending tab
      renderCell: (params) => (
        <Box>
          {/* Enhanced buttons for pending tab */}
          {value === 1 && params.row.status === 'pending' ? (
            <>
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={<ApproveIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleApprove(params.row._id);
                }}
                sx={{ mr: 1, fontSize: '0.75rem', px: 1 }}
              >
                Approve
              </Button>
              <Button
                size="small"
                variant="contained"
                color="error"
                startIcon={<RejectIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleReject(params.row._id);
                }}
                sx={{ fontSize: '0.75rem', px: 1 }}
              >
                Reject
              </Button>
            </>
          ) : (
            // Standard icon buttons for other tabs
            <>
              <IconButton
                size="small"
                color="success"
                onClick={(e) => {
                  e.stopPropagation();
                  handleApprove(params.row._id);
                }}
                disabled={params.row.status === 'approved'}
              >
                <ApproveIcon />
              </IconButton>
              <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  handleReject(params.row._id);
                }}
                disabled={params.row.status === 'rejected'}
              >
                <RejectIcon />
              </IconButton>
              <IconButton
                size="small"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(params.row._id);
                }}
              >
                <EditIcon />
              </IconButton>
              <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(params.row._id);
                }}
              >
                <DeleteIcon />
              </IconButton>
            </>
          )}
        </Box>
      ),
    },
  ];

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.location?.toLowerCase().includes(searchTerm.toLowerCase());
    
    let statusMatch;
    switch (value) {
      case 0: statusMatch = true; break; // All jobs
      case 1: statusMatch = job.status === 'pending'; break;
      case 2: statusMatch = job.status === 'approved'; break; // Active jobs are approved jobs
      case 3: statusMatch = job.status === 'rejected'; break;
      case 4: statusMatch = (job.status === 'closed' || job.status === 'deleted'); break; // Removed jobs are closed or deleted
      default: statusMatch = true; break;
    }
    
    const result = matchesSearch && statusMatch;
    
    // Debug logging for tab 1 (pending jobs)
    if (value === 1) {
      console.log(`Job "${job.title}" - Status: "${job.status}" - Matches: ${result}`);
    }
    
    return result;
  });

  const getFilteredJobs = () => {
    console.log(`Getting filtered jobs for tab ${value}:`);
    console.log(`Total jobs: ${jobs.length}`);
    console.log(`Filtered jobs: ${filteredJobs.length}`);
    console.log('Filtered jobs data:', filteredJobs);
    return filteredJobs;
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={value} onChange={handleChange} aria-label="job management tabs">
          <Tab label="All Jobs" {...a11yProps(0)} />
          <Tab label="Pending Approval" {...a11yProps(1)} />
          <Tab label="Active Jobs" {...a11yProps(2)} />
          <Tab label="Rejected Jobs" {...a11yProps(3)} />
          <Tab label="Removed Jobs" {...a11yProps(4)} />
        </Tabs>
      </Box>

      <Box sx={{ my: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search jobs..."
          value={searchTerm}
          onChange={handleSearch}
          sx={{ mr: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/employer/jobs/create')}
          sx={{ minWidth: 'auto', whiteSpace: 'nowrap' }}
        >
          Add Job
        </Button>
      </Box>

      {/* Tab Panels */}
      {[0, 1, 2, 3, 4].map((tabIndex) => (
        <TabPanel key={tabIndex} value={value} index={tabIndex}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : (
            <Paper sx={{ height: 600, width: '100%' }}>
              {/* Bulk Actions Toolbar */}
              {selectedRows.length > 0 && (
                <Toolbar sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                  <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                    {selectedRows.length} job(s) selected
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {getAvailableBulkActions().map(action => renderBulkActionButton(action))}
                  </Box>
                </Toolbar>
              )}
              <DataGrid
                rows={getFilteredJobs()}
                columns={columns}
                pageSize={10}
                rowsPerPageOptions={[10, 25, 50]}
                checkboxSelection
                disableSelectionOnClick
                getRowId={(row) => row._id}
                onRowClick={(params) => navigate(`/admin/jobs/${params.row._id}`)}
                onSelectionModelChange={(newSelection) => setSelectedRows(newSelection)}
                selectionModel={selectedRows}
                sx={{
                  '& .MuiDataGrid-cell': {
                    borderBottom: '1px solid #e0e0e0',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#f5f5f5',
                    borderBottom: '2px solid #e0e0e0',
                  },
                  '& .MuiDataGrid-row': {
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                  },
                }}
              />
            </Paper>
          )}
        </TabPanel>
      ))}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, action: '', title: '', message: '' })}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            {confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmDialog({ open: false, action: '', title: '', message: '' })}
            color="primary"
          >
            Cancel
          </Button>
          <Button 
            onClick={executeBulkAction} 
            color="primary" 
            variant="contained"
            autoFocus
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Review Dialog for Approve/Reject */}
      <Dialog 
        open={reviewDialog.open} 
        onClose={() => setReviewDialog({ open: false, jobId: null, action: '', reviewNotes: '' })}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          {reviewDialog.action === 'approve' ? 'Approve Job' : 'Reject Job'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {reviewDialog.action === 'approve' 
              ? 'You can optionally add review notes for this approval.' 
              : 'Please provide a reason for rejecting this job.'
            }
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            rows={4}
            label={reviewDialog.action === 'approve' ? 'Review Notes (Optional)' : 'Rejection Reason (Required)'}
            value={reviewDialog.reviewNotes}
            onChange={(e) => setReviewDialog({ ...reviewDialog, reviewNotes: e.target.value })}
            required={reviewDialog.action === 'reject'}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setReviewDialog({ open: false, jobId: null, action: '', reviewNotes: '' })}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleReviewAction}
            variant="contained"
            color={reviewDialog.action === 'approve' ? 'success' : 'error'}
            disabled={reviewDialog.action === 'reject' && !reviewDialog.reviewNotes.trim()}
          >
            {reviewDialog.action === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}